import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, MenuItem, Select, FormControl, InputLabel, Chip, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  DescriptionOutlined as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useDeclarationsAdmin, useFinancialYears, useApproveDeclaration, useRejectDeclaration,
  useEmployers, useGenerateForm16,
} from '../../../hooks/useForm16';
import {
  TABLE_PAPER_SX,
  HEAD_CELL_SX,
  BODY_CELL_SX,
  TABLE_ROW_SX,
} from '../../common/tableStyles';
import ProofLink from '../../common/ProofLink';
import { SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const CANCEL_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' };

// Soft tinted status pill (SUBMITTED/APPROVED/REJECTED/DRAFT).
const chipSx = (fg, bg) => ({ fontWeight: 700, bgcolor: bg, color: fg, border: 'none' });
const statusChipSx = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED') return chipSx('#16a34a', '#f0fdf4');
  if (s === 'SUBMITTED') return chipSx('#16a34a', '#f0fdf4');
  if (s === 'REJECTED') return chipSx('#ef4444', '#fef2f2');
  if (s === 'DRAFT') return chipSx('#6b7280', '#f3f4f6');
  return chipSx('#2f6bff', '#eef4ff');
};

const fmt = (v) => Number(v || 0).toLocaleString('en-IN');

const fmtDateTime = (v) => {
  if (!v) return '—';
  const dt = new Date(v);
  return Number.isNaN(dt.getTime())
    ? '—'
    : dt.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
};

const sumDecl = (d) => Number(d.sec_80c_lic || 0) + Number(d.sec_80c_ppf || 0)
  + Number(d.sec_80c_elss || 0) + Number(d.sec_80c_nsc || 0)
  + Number(d.sec_80c_tuition || 0) + Number(d.sec_80c_home_principal || 0)
  + Number(d.sec_80c_other || 0);

// Case-insensitive regime check (records may store 'NEW' or the default 'new').
const isNewRegime = (d) => String(d?.regime || '').toUpperCase() === 'NEW';

// Just the file name from a (possibly pre-signed S3) URL: drop the ?X-Amz-...
// query string, take the last path segment, and decode %20-style escapes.
const fileNameFromUrl = (url) => {
  const path = String(url || '').split('?')[0];
  const last = path.split('/').pop() || path;
  try { return decodeURIComponent(last); } catch { return last; }
};

// Proof document(s) uploaded for a given section code, rendered as link(s).
const proofLinks = (d, code) => {
  const ps = (d?.proofs || []).filter((p) => p.section_code === code);
  if (!ps.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
  // Show a short "View proof" link (full filename on hover) instead of the raw
  // upload name — so a long name can't overflow its column into the next table.
  return ps.map((p, i) => (
    <ProofLink key={p.id} proof={p}
      label={`View proof${ps.length > 1 ? ` ${i + 1}` : ''}`}
      title={p.description || p.file_name || fileNameFromUrl(p.file)}
      style={{ fontSize: 12, fontWeight: 600, display: 'block', whiteSpace: 'nowrap', color: ACCENT, textDecoration: 'underline' }} />
  ));
};

const ProofHead = () => (
  <TableHead>
    <TableRow>
      <TableCell sx={{ ...HEAD_CELL_SX, width: '48%' }}>Particulars</TableCell>
      <TableCell sx={{ ...HEAD_CELL_SX, width: '22%' }} align="right">Amount</TableCell>
      <TableCell sx={{ ...HEAD_CELL_SX, width: '30%' }}>Proof</TableCell>
    </TableRow>
  </TableHead>
);

// One review row: label, amount, and the proof uploaded for `code` (null = no proof column content).
const declRow = (d, label, amount, code) => (
  <TableRow sx={TABLE_ROW_SX}>
    <TableCell sx={BODY_CELL_SX}>{label}</TableCell>
    <TableCell sx={BODY_CELL_SX} align="right">{amount}</TableCell>
    <TableCell sx={BODY_CELL_SX}>{code ? proofLinks(d, code) : <Typography variant="caption" color="text.secondary">—</Typography>}</TableCell>
  </TableRow>
);

export default function DeclarationsAdminReview() {
  const { data: fys = [] } = useFinancialYears();
  const [statusFilter, setStatusFilter] = useState('SUBMITTED');
  const [fyId, setFyId] = useState('');
  const params = useMemo(() => {
    const p = {};
    if (statusFilter) p.status = statusFilter;
    if (fyId) p.fy_id = fyId;
    return p;
  }, [statusFilter, fyId]);
  const { data: list = [], refetch } = useDeclarationsAdmin(params);
  const approve = useApproveDeclaration();
  const reject = useRejectDeclaration();
  const [picked, setPicked] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Bridge to step 2: generate the Form 16 straight from an approved declaration.
  const navigate = useNavigate();
  const { data: employers = [] } = useEmployers();
  const activeEmployer = employers.find((e) => e.is_active) || employers[0];
  const generate = useGenerateForm16();
  const [genError, setGenError] = useState(null);
  const fyIdForLabel = (label) => fys.find((f) => f.label === label)?.id;
  const generateForm16 = (decl) => {
    setGenError(null);
    generate.mutate(
      { employeeNo: decl.employee_no, fyId: fyIdForLabel(decl.fy_label), employerId: activeEmployer?.id },
      {
        onSuccess: (res) => { setPicked(null); if (res?.id) navigate(`/payroll/form16/${res.id}`); },
        onError: (err) => setGenError(err?.response?.data?.error || err.message),
      },
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FactCheckIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Tax Declarations — Admin Review
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Review, approve and generate Form 16 from employee tax declarations.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filters card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SUBMITTED">Submitted</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}>
              <InputLabel>FY</InputLabel>
              <Select label="FY" value={fyId} onChange={(e) => setFyId(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="">All</MenuItem>
                {fys.map((fy) => (
                  <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table card */}
      <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
              <TableCell sx={HEAD_CELL_SX}>FY</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Regime</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">80C Total</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">80D</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">HRA Rent</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Submitted</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 8, border: 'none' }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <DescriptionIcon />
                    </Box>
                    <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No declarations match.</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : list.map((d) => (
              <TableRow key={d.id} hover sx={TABLE_ROW_SX}>
                <TableCell sx={BODY_CELL_SX}>
                  <Typography variant="body2" fontWeight={600}>{d.employee_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{d.employee_no}</Typography>
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>{d.fy_label}</TableCell>
                <TableCell sx={BODY_CELL_SX}>{d.regime}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">{isNewRegime(d) ? 'NA' : fmt(sumDecl(d))}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">{isNewRegime(d) ? 'NA' : fmt(Number(d.sec_80d_self || 0) + Number(d.sec_80d_parents || 0))}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">{isNewRegime(d) ? 'NA' : fmt(d.rent_paid_annual)}</TableCell>
                <TableCell sx={BODY_CELL_SX}><Chip size="small" label={d.status} sx={statusChipSx(d.status)} /></TableCell>
                <TableCell sx={BODY_CELL_SX}>{fmtDateTime(d.submitted_at)}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">
                  <Button size="small" onClick={() => setPicked(d)} sx={{ ...GRADIENT_BTN, color: '#fff', px: 2 }}>Review</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* zIndex above the fixed admin header (HeaderComponent, z-index 1301) so the
          dialog title isn't clipped under it (MUI modal default is 1300). */}
      <Dialog open={!!picked} onClose={() => setPicked(null)} fullWidth maxWidth="lg"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
        PaperProps={{ sx: { borderRadius: '22px' } }}>
        {picked && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {picked.employee_name} ({picked.employee_no}) — FY {picked.fy_label}
              <Chip size="small" sx={{ ml: 2, ...statusChipSx(picked.status) }} label={picked.status} />
              <Chip size="small" sx={{ ml: 1, ...statusChipSx('') }}
                label={`${isNewRegime(picked) ? 'New' : 'Old'} regime`} />
            </DialogTitle>
            <DialogContent dividers sx={{ '& table': { tableLayout: 'fixed', width: '100%' } }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {!isNewRegime(picked) && (<>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Section 80C</Typography>
                  <Table size="small">
                    <ProofHead />
                    <TableBody>
                      {declRow(picked, 'LIC', fmt(picked.sec_80c_lic), '80C_LIC')}
                      {declRow(picked, 'PPF', fmt(picked.sec_80c_ppf), '80C_PPF')}
                      {declRow(picked, 'ELSS', fmt(picked.sec_80c_elss), '80C_ELSS')}
                      {declRow(picked, 'NSC', fmt(picked.sec_80c_nsc), '80C_NSC')}
                      {declRow(picked, 'Tuition', fmt(picked.sec_80c_tuition), '80C_TUITION')}
                      {declRow(picked, 'Home Loan Principal', fmt(picked.sec_80c_home_principal), '80C_HOME')}
                      {declRow(picked, 'Other', fmt(picked.sec_80c_other), '80C_OTHER')}
                      <TableRow sx={TABLE_ROW_SX}><TableCell sx={BODY_CELL_SX}><b>Total (capped 1.5L)</b></TableCell><TableCell sx={BODY_CELL_SX} align="right"><b>{fmt(Math.min(150000, sumDecl(picked)))}</b></TableCell><TableCell sx={BODY_CELL_SX} /></TableRow>
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Other Chapter VI-A</Typography>
                  <Table size="small">
                    <ProofHead />
                    <TableBody>
                      {declRow(picked, '80CCC', fmt(picked.sec_80ccc), null)}
                      {declRow(picked, '80CCD(1)', fmt(picked.sec_80ccd_1), null)}
                      {declRow(picked, '80CCD(1B)', fmt(picked.sec_80ccd_1b), null)}
                      {declRow(picked, '80CCD(2)', fmt(picked.sec_80ccd_2), '80CCD2')}
                      {declRow(picked, '80D — Self', fmt(picked.sec_80d_self), '80D_SELF')}
                      {declRow(picked, `80D — Parents${picked.sec_80d_parents_senior ? ' (senior)' : ''}`, fmt(picked.sec_80d_parents), '80D_PARENTS')}
                      {declRow(picked, '80E', fmt(picked.sec_80e), '80E')}
                      {declRow(picked, '80G', fmt(picked.sec_80g), '80G')}
                      {declRow(picked, '80TTA', fmt(picked.sec_80tta), '80TTA')}
                      {declRow(picked, `Rent paid (annual)${picked.metro_city ? ' (metro)' : ''}`, fmt(picked.rent_paid_annual), 'HRA_RENT')}
                      {declRow(picked, 'Sec 24(b) home-loan interest', fmt(picked.sec_24b_interest), '24B')}
                    </TableBody>
                  </Table>
                </Grid>
                </>)}
                {isNewRegime(picked) && (<>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Income considered</Typography>
                  <Table size="small">
                    <ProofHead />
                    <TableBody>
                      {declRow(picked, 'Previous employer salary', fmt(picked.prev_employer_salary), 'PREV_EMP_SALARY')}
                      {declRow(picked, 'Previous employer TDS', fmt(picked.prev_employer_tds), 'PREV_EMP_TDS')}
                      {declRow(picked, 'House property (let-out) income/loss', fmt(picked.house_property_letout), 'HP_LETOUT')}
                      {declRow(picked, 'Other income — interest', fmt(picked.other_income_interest), 'OI_INTEREST')}
                      {declRow(picked, 'Other income — rental', fmt(picked.other_income_rental), 'OI_RENTAL')}
                      {declRow(picked, 'Other income — other', fmt(picked.other_income_other), 'OI_OTHER')}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Deductions (New Regime)</Typography>
                  <Table size="small">
                    <ProofHead />
                    <TableBody>
                      <TableRow sx={TABLE_ROW_SX}><TableCell sx={BODY_CELL_SX}>Standard deduction</TableCell><TableCell sx={BODY_CELL_SX} align="right">75,000 (auto)</TableCell><TableCell sx={BODY_CELL_SX} /></TableRow>
                      {declRow(picked, '80CCD(2) — Employer NPS', fmt(picked.sec_80ccd_2), '80CCD2')}
                    </TableBody>
                  </Table>
                </Grid>
                </>)}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Proofs</Typography>
                  {picked.proofs?.length === 0 ? (
                    <Alert severity="warning" sx={{ borderRadius: '12px' }}>No proofs uploaded.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      {picked.proofs?.map((p) => (
                        <Stack key={p.id} direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={p.section_code} sx={statusChipSx('')} />
                          <ProofLink proof={p} label={p.description || p.file_name || fileNameFromUrl(p.file)} title={p.description || p.file_name || fileNameFromUrl(p.file)} style={{ color: ACCENT }} />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Grid>
                {picked.status === 'REJECTED' && picked.rejection_reason && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ borderRadius: '12px' }}>Rejected: {picked.rejection_reason}</Alert>
                  </Grid>
                )}
              </Grid>
              {picked.status === 'SUBMITTED' && (
                <Box sx={{ mt: 2 }}>
                  <TextField fullWidth size="small" label="Rejection reason (only if rejecting)"
                    value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }} />
                </Box>
              )}
            </DialogContent>
            {genError && (
              <Alert severity="error" sx={{ mx: 3, mb: 1, borderRadius: '12px' }} onClose={() => setGenError(null)}>
                Could not generate Form 16: {genError}
              </Alert>
            )}
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setPicked(null)} sx={CANCEL_BTN}>Close</Button>
              {picked.status === 'SUBMITTED' && (
                <>
                  <Button variant="contained" color="error" disableElevation disabled={!rejectReason || reject.isPending}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                    onClick={() => reject.mutate({ id: picked.id, reason: rejectReason }, {
                      onSuccess: () => { setPicked(null); setRejectReason(''); refetch(); },
                    })}
                  >Reject</Button>
                  <Button variant="contained" color="success" disableElevation disabled={approve.isPending}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                    onClick={() => approve.mutate({ id: picked.id }, {
                      // Keep the dialog open and flip to APPROVED so the
                      // "Generate Form 16" button appears right away.
                      onSuccess: () => { setPicked((p) => (p ? { ...p, status: 'APPROVED' } : p)); refetch(); },
                    })}
                  >Approve</Button>
                </>
              )}
              {picked.status === 'APPROVED' && (
                <Button variant="contained" disableElevation
                  disabled={generate.isPending || !activeEmployer || !fyIdForLabel(picked.fy_label)}
                  sx={GRADIENT_BTN}
                  onClick={() => generateForm16(picked)}
                >
                  {generate.isPending ? 'Generating…' : 'Generate Form 16 →'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
